import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import Select from "ds/components/Select";
import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import DragDropList from "components/DragDropList";
import {
  ColumnMode,
  DragDropOnChangeProps,
  RenderItemContentProps,
} from "components/DragDropList/types";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import EmptyState from "ds/components/EmptyState";
import { PinFilled } from "components/icons/generated";
import FormField from "ds/components/Form/Field";
import useMainNavigationPrimaryItems from "components/MainNavigation/hooks/usePrimaryItems";
import { trackSegmentEvent } from "shared/Analytics";
import Card from "ds/components/Card";
import DragDropListDefaultItem from "components/DragDropList/DefaultItem";

import useMainNavigationStore from "../../../stores/useMainNavigationStore";
import useMainNavigationPinnedItems from "../../../hooks/usePinnedItems";
import useMainNavigationDefaultView from "../../../hooks/useDefaultView";

const MainNavigationSettingsPage = () => {
  const pinned = useMainNavigationPinnedItems();
  const { menuItems } = useMainNavigationPrimaryItems();
  const { defaultViewItem, setDefaultViewItemId } = useMainNavigationDefaultView();

  const { unpinMenuItem, reorderPinnedMenuItems, moveItemUp, moveItemDown } =
    useMainNavigationStore(
      useShallow((state) => ({
        unpinMenuItem: state.unpinMenuItem,
        reorderPinnedMenuItems: state.reorderPinnedMenuItems,
        moveItemUp: state.moveItemUp,
        moveItemDown: state.moveItemDown,
      }))
    );

  const handleReorderItems = useCallback(
    ({ first }: DragDropOnChangeProps<string, { title: string }>) => {
      const orderedItemIds = first.map((item) => item.value);
      reorderPinnedMenuItems(orderedItemIds);
    },
    [reorderPinnedMenuItems]
  );

  const firstColumn = useMemo(
    () =>
      pinned.map(
        (item, index) =>
          item && {
            value: item.id,
            props: {
              title: item.title,
              index,
            },
          }
      ),
    [pinned]
  );

  const renderItemContent = useCallback(
    ({
      id,
      dragging,
      handleRef,
      handleListeners,
      handleAttributes,
      disabled,
      props,
    }: RenderItemContentProps<string, { title: string; index: number }>) => {
      return (
        <DragDropListDefaultItem
          dragging={dragging}
          handleRef={handleRef}
          handleListeners={handleListeners}
          handleAttributes={handleAttributes}
          disabled={disabled}
          titleComponent={
            props ? (
              <Box direction="row" gap="small" justify="between" align="center" fullWidth>
                <Typography tag="span" variant="p-t7">
                  {props.title}
                </Typography>

                <DropdownMenuEllipsis tooltip="Item actions" buttonVariant="ghost">
                  <DropdownMenuItem
                    onAction={() => {
                      unpinMenuItem(id);
                      trackSegmentEvent("MainNavigation: Settings -> unpin item");
                    }}
                  >
                    Unpin
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onAction={() => {
                      moveItemUp(id);
                      trackSegmentEvent("MainNavigation: Settings -> reorder item");
                    }}
                    isDisabled={props.index === 0}
                  >
                    Move up
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onAction={() => {
                      moveItemDown(id);
                      trackSegmentEvent("MainNavigation: Settings -> reorder item");
                    }}
                    isDisabled={props.index === pinned.length - 1}
                  >
                    Move down
                  </DropdownMenuItem>
                </DropdownMenuEllipsis>
              </Box>
            ) : null
          }
        />
      );
    },
    [unpinMenuItem, moveItemUp, moveItemDown, pinned.length]
  );

  const handleDefaultViewChange = useCallback(
    (itemId: string) => {
      setDefaultViewItemId(itemId);
    },
    [setDefaultViewItemId]
  );

  const defaultViewOptions = useMemo(
    () =>
      menuItems.map((item) => ({
        value: item.id,
        label: item.title,
      })),
    [menuItems]
  );

  const isEmpty = firstColumn.length === 0;

  return (
    <Box direction="column" limitWidth="medium" gap="x-large">
      <Card>
        <FormField
          label="Default view"
          tooltipInfo="Select a view which will be the first view you see when you click Spacelift logo."
          tooltipWidthMode="maxWidthSm"
          noMargin
        >
          <Select
            value={defaultViewItem.id}
            items={defaultViewOptions}
            onChange={handleDefaultViewChange}
          />
        </FormField>
      </Card>

      <Card variant="secondary" titleTag="h3" title="Pinned sidebar items">
        {!isEmpty && (
          <DragDropList
            columnMode={ColumnMode.Single}
            onChange={handleReorderItems}
            onSelect={() => undefined}
            firstColumn={firstColumn}
            renderItemContent={renderItemContent}
            vertical
            verticalAxisOnly
          />
        )}

        {isEmpty && (
          <EmptyState
            title="You don’t have any pinned items yet"
            icon={PinFilled}
            caption="Pinned items can be used to quickly access frequently used items."
            padding="x-large"
            margin="x-large"
          />
        )}
      </Card>
    </Box>
  );
};

export default MainNavigationSettingsPage;
